import { z } from 'zod'
import { applyMask } from '~/packages/general/detailedInformation/informationMasks'
import { formatDate } from '~/prix/types/date'
import { formatAsMoney } from '~/prix/utils/types/number'

export const waterSourceApplicationReportTypeSchema = z.enum(['payment', 'request'])

export type WaterSourceApplicationReportType = z.infer<
  typeof waterSourceApplicationReportTypeSchema
>

export interface WaterSourceApplicationReportData {
  id: string
  incentiveType?: string | null
  value?: number | null
  bankHolderName?: string | null
  bankHolderDocument?: string | null
  bankName?: string | null
  bankBranch?: string | null
  bankAccount?: string | null
  observation?: string | null
  createdAt?: Date | string | null
  requesterFullName?: string | null
  requesterSocialName?: string | null
  requesterCpf?: string | null
  requesterFullAddress?: string | null
}

export interface WaterSourceApplicationReportProps {
  type: WaterSourceApplicationReportType
  data?: WaterSourceApplicationReportData | null
  templateTitle: string
}

export const waterSourceApplicationReportColor = '#00A859'

export const waterSourceApplicationReportTitleMap: Record<
  WaterSourceApplicationReportType,
  string
> = {
  payment: 'TERMO DE ENCAMINHAMENTO PARA PAGAMENTO DE INCENTIVO',
  request: 'REQUERIMENTO DE SOLICITAÇÃO DE INCENTIVO DO PROGRAMA DE FONTES DE ÁGUA',
}

export function buildWaterSourceApplicationReportFileBaseName(
  type: WaterSourceApplicationReportType,
) {
  return type === 'payment'
    ? 'water-source-application-payment-report'
    : 'water-source-application-request-report'
}

export function formatWaterSourceApplicationReportDate(value?: Date | string | null) {
  if (!value) {
    return 'Não informado'
  }

  const parsedValue = value instanceof Date ? value : new Date(value)

  if (Number.isNaN(parsedValue.getTime())) {
    return String(value)
  }

  return formatDate(parsedValue)
}

export function formatWaterSourceApplicationText(value?: string | null) {
  return value?.trim() || 'Não informado'
}

export function formatWaterSourceApplicationMoney(value?: number | string | null) {
  if (value === null || value === undefined) {
    return 'Não informado'
  }

  const numericValue = Number(value)

  if (!Number.isFinite(numericValue)) {
    return 'Não informado'
  }

  return formatAsMoney(numericValue)
}

export function buildWaterSourceApplicationIncentiveTypeLabel(value?: string | null) {
  if (!value) {
    return 'Não informado'
  }

  const incentiveTypeLabelMap: Record<string, string> = {
    fonte_protegida: 'Fonte protegida',
    fonte_caxambu: 'Fonte caxambu',
    protectedFountain: 'Fonte protegida',
    caxambuFountain: 'Fonte caxambu',
  }

  return incentiveTypeLabelMap[value] ?? value
}

export function formatWaterSourceApplicationCpf(value?: string | null) {
  if (!value) {
    return 'Não informado'
  }

  return applyMask(value, 'cpf')
}

export function formatWaterSourceApplicationHolderDocument(value?: string | null) {
  if (!value?.trim()) {
    return 'Não informado'
  }

  const numericValue = value.replace(/\D/g, '')

  if (numericValue.length === 11) {
    return applyMask(numericValue, 'cpf')
  }

  if (numericValue.length === 14) {
    return applyMask(numericValue, 'cnpj')
  }

  return value.trim()
}

export function buildWaterSourceApplicationRequesterName(
  data?: WaterSourceApplicationReportData | null,
) {
  const requesterFullName = data?.requesterFullName?.trim()
  const requesterSocialName = data?.requesterSocialName?.trim()

  if (requesterSocialName) {
    return requesterSocialName
  }

  if (!requesterSocialName && requesterFullName) {
    return requesterFullName
  }

  return requesterSocialName || requesterFullName || 'Não informado'
}
